<script>
import { mapGetters } from 'vuex';
import { sensorInAlarm } from '../../../util/util';
import { TSTAR_GEOTYPE_CHECKIN_ARROW } from '../../../../../common/defs';

export default {
    name: 'SensorTableEntry',

    props: {
        sensor: Object,
        sensorName: String,
        onNameChanged: Function,
        onMonitorChanged: Function,
        monitored: Boolean,
        pendingMonitored: Boolean,
        tableIndex: Number,
        readonly: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        ...mapGetters(['isCampaignViewer']),

        inAlarm() {
            return sensorInAlarm(this.sensor);
        },

        getSecurityIcon() {
            if (this.sensor.sensor_type == 'SENSOR_ANALOG_INPUT') {
                return '<img src="/assets/cable.svg" class="icon" />';
            } else if (this.sensor.security) {
                return '<i class="fas fa-lock" />';
            } else {
                return '<i class="fas fa-unlock" />';
            }
        },

        showPendingMonitored() {
            return this.pendingMonitored != null;
        },

        getBatteryLevel() {
            if (this.sensor.sensor_type == 'SENSOR_ANALOG_INPUT') {
                return '--';
            } else {
                return this.sensor.battery_level;
            }
        },

        getRSSI() {
            if (this.sensor.sensor_type == 'SENSOR_ANALOG_INPUT') {
                return '--';
            } else {
                return this.sensor.rssi;
            }
        },

        getTimestamp() {
            if (this.sensor.sensor_type == 'SENSOR_ANALOG_INPUT') {
                return '--';
            } else {
                return (
                    ((Date.now() / 1000) | 0) -
                    this.sensor.last_report_timestamp
                );
            }
        },
    },

    methods: {
        onLocalNameChanged(event) {
            const value = event.target?.value ?? !!event;
            this.onNameChanged(this.sensor.node_id, value);
        },

        setSensorMonitored(event) {
            const value = event.target?.checked ?? !!event;
            this.onMonitorChanged(this.sensor.node_id, value);
        },
    },
};
</script>

<template>
    <tr :class="{ odd: tableIndex % 2 === 1, alert: inAlarm }">
        <td>{{ sensor.node_id }}</td>
        <td>
            <InputBox
                v-if="!readonly"
                hoverShow
                :value="sensorName"
                @input="onLocalNameChanged"
                :disabled="isCampaignViewer"
            />
            <div v-else style="margin: 5px 0; min-height: 24px">
                {{ sensorName }}
            </div>
        </td>
        <td>
            {{
                sensor.sensor_type ? sensor.sensor_type.split(/_(.*)/s)[1] : ''
            }}
        </td>
        <td>
            <div class="box-container">
                <Checkbox
                    v-if="!readonly"
                    @change="setSensorMonitored($event)"
                    :checked="monitored"
                    :disabled="isCampaignViewer"
                />
                <i v-else-if="monitored" class="fas fa-check" />
                <div
                    v-if="showPendingMonitored"
                    class="pending-bubble"
                    v-tooltip.top="
                        $t('valuePending', { value: pendingMonitored })
                    "
                >
                    <i class="fas fa-paper-plane" />
                </div>
            </div>
        </td>
        <td>{{ getBatteryLevel }}</td>
        <td>{{ getRSSI }}</td>
        <td>{{ getTimestamp }}</td>
        <td class="secure-column" v-html="getSecurityIcon"></td>
    </tr>
</template>

<style lang="scss" scoped>
@use 'sass:color';
@use '../../../palette.scss' as *;

tr {
    td {
        padding: 0 10px;

        .box-container {
            display: flex;
            align-items: center;

            .pending-bubble {
                margin-left: 15px;
                height: 15px;
                width: 15px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 0.5em;
                border-radius: 50%;
                border: 1px solid $theme-light-blue;
                color: $theme-light-blue;
                padding-right: 1px;

                &:hover {
                    background-color: $theme-light-blue;
                    color: white;
                    cursor: pointer;
                }
            }
        }

        :deep(i.fas) {
            &.fa-lock {
                color: $theme-green;
            }

            &.fa-unlock {
                color: $theme-red;
            }
        }

        .icon {
            width: 16px;
            height: 16px;
            vertical-align: middle;
        }

        .secure-column {
            text-align: center;
        }
    }
}
</style>
